import { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, CheckCheck, Bell, Clock, FileText, CheckCircle2, XCircle } from "lucide-react";
import type { Notification } from "./NotificationBell";

interface NotificationDropdownProps {
  notifications: Notification[];
  loading: boolean;
  onClose: () => void;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
}

const NotificationDropdown = ({
  notifications,
  loading,
  onClose,
  onMarkAsRead,
  onMarkAllAsRead
}: NotificationDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'request_pending_manager':
      case 'request_pending_hr':
      case 'new_request':
        return <Clock className="h-4 w-4" />;
      case 'manager_approved':
      case 'request_approved':
        return <CheckCircle2 className="h-4 w-4" />;
      case 'manager_rejected':
      case 'request_rejected':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getNotificationColor = (type: string) => {
    if (type.includes('approved') || type === 'manager_approved') {
      return 'text-green-600 bg-green-100';
    }
    if (type.includes('rejected') || type === 'manager_rejected') {
      return 'text-red-600 bg-red-100';
    }
    if (type.includes('pending') || type === 'new_request') {
      return 'text-amber-600 bg-amber-100';
    }
    return 'text-blue-600 bg-blue-100';
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR');
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    if (!notification.read_at) {
      onMarkAsRead(notification.id);
    }

    // Navigate based on metadata or notification type
    const metadata = notification.metadata as { navigation_url?: string; request_id?: string } | null;
    const navUrl = metadata?.navigation_url;
    
    if (navUrl) {
      onClose();
      navigate(navUrl);
    } else {
      // Fallback navigation based on type
      const type = notification.type;
      if (type === 'request_pending_manager' || type === 'new_request') {
        onClose();
        navigate('/employee-dashboard?tab=gestion-equipe');
      } else if (type === 'request_pending_hr') {
        onClose();
        navigate('/company-dashboard?tab=demandes');
      } else if (type.includes('approved') || type.includes('rejected')) {
        onClose();
        navigate('/employee-dashboard?tab=trips');
      }
    }
  };

  const unreadNotifications = notifications.filter(n => !n.read_at);

  return (
    <Card 
      ref={dropdownRef}
      className="absolute right-0 top-12 w-80 sm:w-96 z-50 shadow-lg"
    >
      <CardHeader className="py-3 px-4 border-b">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Notifications
          </CardTitle>
          {unreadNotifications.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              onClick={onMarkAllAsRead}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Tout marquer lu
            </Button>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Aucune notification</p>
          </div>
        ) : (
          <ScrollArea className="max-h-80">
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 hover:bg-muted/50 cursor-pointer transition-colors ${
                    !notification.read_at ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex gap-3">
                    <div className={`rounded-full p-2 ${getNotificationColor(notification.type)}`}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium truncate ${!notification.read_at ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {notification.title}
                        </p>
                        {!notification.read_at && (
                          <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-1.5"></span>
                        )}
                      </div>
                      {notification.body && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                          {notification.body}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatRelativeTime(notification.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationDropdown;